import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Building,
  MapPin,
  Star,
  ArrowRight,
  Users,
  Key
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const CocoaPage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Regular cleaning for Cocoa families and homeowners. Weekly or bi-weekly service with the same trusted team."
    },
    {
      title: "Deep Cleaning",
      description: "Comprehensive cleaning for established Cocoa homes. Perfect for seasonal refreshes or preparing a home for sale."
    },
    {
      title: "Move-In / Move-Out Cleaning",
      description: "Inspection-ready cleaning for Cocoa home transitions, helping renters, homeowners, and Realtors."
    },
    {
      title: "Real Estate & Listing Prep",
      description: "Pre-listing cleaning and staging preparation for Cocoa properties hitting the market."
    }
  ];

  const perfectFor = [
    "Homeowners in historic Cocoa neighborhoods",
    "Families in growing residential areas",
    "Realtors preparing Cocoa listings for the market",
    "Landlords managing rental properties",
    "Properties near Cocoa Village and riverfront",
    "Residents along US-1 and Dixon Boulevard corridors"
  ];

  const faqs = [
    {
      question: "Do you serve all of Cocoa?",
      answer: "Yes. We serve all Cocoa neighborhoods—from historic Cocoa Village to the residential areas near Peachtree and along US-1, Dixon Boulevard, and throughout the community. Cocoa is diverse, and we're familiar with the entire area."
    },
    {
      question: "Can you help Realtors with listing preparation in Cocoa?",
      answer: "Absolutely. We work with Cocoa Realtors preparing homes for the market. Pre-listing deep cleans help properties show better and photograph well. We're flexible and understand the timelines involved in real estate."
    },
    {
      question: "What makes Pierce Premier different from other cleaning companies serving Cocoa?",
      answer: "We're locally owned by Bob and Heather, not a franchise. You'll have the same cleaners every visit, and Heather's 15+ years of experience means we deliver consistent, professional results. We're accountable and easy to reach."
    },
    {
      question: "Do you work with landlords and property managers in Cocoa?",
      answer: "Yes. We handle turnover cleaning for rental properties throughout Cocoa. Whether it's a single unit or multiple properties, we're reliable and work within your timelines."
    },
    {
      question: "Can you accommodate urgent or last-minute requests?",
      answer: "We do our best. Call us at 321-408-1347 and let us know what you need. As a small, locally-owned business, we're often more flexible than larger companies."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Cocoa, FL | Pierce Premier",
      description: "Trusted house cleaning in Cocoa, FL. Residential, real estate, and rental property services. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/cocoa"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Cocoa" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Cocoa",
      description: "Professional house cleaning, real estate prep, and rental property turnover services in Cocoa, FL."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Cocoa, FL | Pierce Premier"
        description="Trusted house cleaning in Cocoa, FL. Residential, real estate prep, rental turnovers. Same team, 15+ years experience, fully insured. Call 321-408-1347."
        canonical="/areas/cocoa"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-amber-600" />
                  <Badge className="bg-amber-600 text-white">Historic & Growing</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Cocoa, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Cocoa blends historic charm with modern growth—from Cocoa Village along the riverfront to established residential neighborhoods and newer developments. Pierce Premier serves this diverse community with cleaning services for homeowners, Realtors, and property managers who need reliability they can count on.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you're maintaining a family home, preparing a property for sale, or managing rental turnovers, we bring the same commitment to quality and professionalism to every job.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-amber-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-amber-600 text-amber-600 px-8 py-4 rounded-lg font-semibold hover:bg-amber-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=800&q=80" 
                  alt="House cleaning services in Cocoa, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Cocoa Residents and Professionals Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team Every Time</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. Whether you're a homeowner with recurring service or a Realtor with multiple listings, you'll have the same cleaners handling your properties. That consistency matters.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                    <Key className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Real Estate Partnerships</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We work with Cocoa Realtors and property managers who need reliable cleaning for listings, turnovers, and closings. Fast timelines, professional results, and direct communication with Bob and Heather.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Locally Owned & Accountable</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bob and Heather personally oversee every job. We're not a franchise—we're a local business that stands behind our work and responds when you need us.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience to every Cocoa property. We know what quality looks like, and we deliver it consistently.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving Cocoa
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Cocoa has character—historic neighborhoods near the riverfront, growing residential areas, and a real estate market that keeps things interesting. It's the kind of community where you see both long-time residents and new families moving in, and we appreciate serving both.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Whether you're maintaining a home you've lived in for years or preparing a property for new owners, we're glad to be part of keeping Cocoa homes clean and well-cared-for.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Services for Cocoa Properties
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-amber-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Perfect For Cocoa
              </h2>
              <p className="text-gray-600">Pierce Premier is trusted by:</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {perfectFor.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-amber-100">
                  <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Cocoa Areas We Serve
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Cocoa Village",
                "Riverfront Area",
                "Peachtree Area",
                "US-1 Corridor",
                "Dixon Boulevard",
                "King Street Area"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Codes:</span> 32922, 32926, 32927
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                We Also Serve Nearby Communities
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Rockledge", link: "/areas/rockledge" },
                { name: "Merritt Island", link: "#" },
                { name: "Cocoa Beach", link: "/areas/cocoa-beach" },
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Viera", link: "/areas/viera" },
                { name: "Palm Bay", link: "/areas/palm-bay" }
              ].map((city, idx) => (
                city.link === "#" ? (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <span className="text-gray-600">{city.name}</span>
                  </div>
                ) : (
                  <Link key={idx} to={city.link} className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200 hover:border-amber-400 transition-colors text-center group">
                    <span className="text-amber-700 font-semibold group-hover:text-amber-900">{city.name}</span>
                    <ArrowRight className="w-4 h-4 inline-block ml-1 text-amber-600" />
                  </Link>
                )
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Cocoa Clients
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <CTASection 
          title="Ready to Work with Pierce Premier?"
          description="Serving Cocoa homeowners, Realtors, and property managers with reliable cleaning they can count on."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default CocoaPage;